import React from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useTranslation } from "react-i18next";
import { sanitizeAadharInput, sanitizePanInput } from "../../../helpers/regex";
import CustomerSearchSelect from "../../../components/Common/CustomerSearchSelect";

const VerificationModal = ({
  show,
  existingCustomers,
  aadharCard,
  setAadharCard,
  panCard,
  setPanCard,
  matchingCustomers,
  selectedExistingCustomer,
  setSelectedExistingCustomer,
  selectedCustomerFromDropdown,
  isVerifying,
  verificationMode,
  setVerificationMode,
  handleCustomerSelect,
  handleVerificationNext,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={show}
      toggle={() => { }}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <ModalHeader>
        <h5 className="mb-0">{t("customer.add.verificationModal.title")}</h5>
      </ModalHeader>
      <ModalBody>
        <Form>
          {/* Manual Entry Section */}
          <div>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="required">
                    {t("customer.add.verificationModal.aadharCardLabel")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={aadharCard}
                    onChange={(e) => {
                      const value = sanitizeAadharInput(e.target.value);
                      setAadharCard(value);
                      setVerificationMode("manual");
                    }}
                    placeholder={t("customer.add.verificationModal.aadharCardPlaceholder")}
                    maxLength="12"
                    disabled={isVerifying || verificationMode === "select"}
                  />
                  <Form.Text className="text-muted">
                    {t("customer.add.verificationModal.aadharCardHelp")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {matchingCustomers.length > 0 && verificationMode === "manual" && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="required">
                      {t("customer.add.verificationModal.selectExistingCustomerLabel")}
                    </Form.Label>
                    <CustomerSearchSelect
                      inputId="verification-matching-customer"
                      customers={matchingCustomers}
                      value={selectedExistingCustomer}
                      onChange={(customerId) => {
                        setSelectedExistingCustomer(customerId);
                        handleCustomerSelect(customerId);
                      }}
                      disabled={isVerifying}
                      isInvalid={!selectedExistingCustomer}
                      placeholder={t("customer.add.verificationModal.selectCustomerOption")}
                    />
                    <Form.Text className="text-danger">
                      {t("customer.add.verificationModal.aadharExistsError")}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    {t("customer.add.verificationModal.panCardLabel")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={panCard}
                    onChange={(e) => {
                      const value = sanitizePanInput(e.target.value);
                      setPanCard(value);
                      setVerificationMode("manual");
                    }}
                    placeholder={t("customer.add.verificationModal.panCardPlaceholder")}
                    maxLength="10"
                    style={{ textTransform: "uppercase" }}
                    disabled={isVerifying || verificationMode === "select"}
                  />
                  <Form.Text className="text-muted">
                    {t("customer.add.verificationModal.panCardHelp")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* OR Divider */}
          <div className="d-flex align-items-center my-4">
            <div className="flex-grow-1" style={{ height: "1px", backgroundColor: "#dee2e6" }} />
            <span className="mx-3 text-muted fw-bold">{t("customer.add.verificationModal.orDivider")}</span>
            <div className="flex-grow-1" style={{ height: "1px", backgroundColor: "#dee2e6" }} />
          </div>

          {/* Select Existing Customer Section */}
          <div>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="required">
                    {t("customer.add.verificationModal.selectExistingCustomerLabel")}
                  </Form.Label>
                  <CustomerSearchSelect
                    inputId="verification-existing-customer"
                    customers={existingCustomers}
                    value={selectedCustomerFromDropdown}
                    onChange={(customerId) => {
                      handleCustomerSelect(customerId);
                      setVerificationMode("select");
                    }}
                    disabled={isVerifying}
                    isInvalid={verificationMode === "select" && !selectedCustomerFromDropdown}
                    placeholder={t("customer.add.verificationModal.selectExistingCustomerOption")}
                  />
                  <Form.Text className="text-muted">
                    {t("customer.add.verificationModal.selectExistingCustomerHelp")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {selectedCustomerFromDropdown && verificationMode === "select" && (
              <Row className="mb-3">
                <Col md={12}>
                  <div className="alert alert-info">
                    <strong>{t("customer.add.verificationModal.selectedCustomerDetails")}</strong>
                    <br />
                    {(() => {
                      const customer = existingCustomers.find(
                        (c) => c.borrowerId == selectedCustomerFromDropdown
                      );
                      return customer ? (
                        <>
                          <strong>{t("customer.add.verificationModal.nameLabel")}</strong> {customer.name}
                          <br />
                          <strong>{t("customer.add.verificationModal.aadharLabel")}</strong>{" "}
                          {customer.aadharCard || t("customer.add.verificationModal.notAvailable")}
                          <br />
                          <strong>{t("customer.add.verificationModal.panLabel")}</strong>{" "}
                          {customer.panCard || t("customer.add.verificationModal.notAvailable")}
                        </>
                      ) : null;
                    })()}
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleVerificationNext} disabled={isVerifying}>
          {isVerifying ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {t("customer.add.verificationModal.verifying")}
            </>
          ) : (
            t("customer.add.verificationModal.next")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default VerificationModal;
